<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title
                color="blue-grey-lighten-4"
                class="panel-header"
                >Search Form</v-expansion-panel-title
              >
              <v-expansion-panel-text>
                <v-form @submit.prevent="submit">
                  <v-row class="px-2 pt-4 pb-0">
                    <v-col lg="3" md="4" cols="12">
                      <v-autocomplete
                        label="Warehouse"
                        variant="outlined"
                        density="compact"
                        bg-color="grey-lighten-4"
                        v-model="warehouse_id.value.value"
                        :items="getWarehouseList"
                        :item-title="(item) => `${item.name}`"
                        item-value="id"
                        :error-messages="warehouse_id.errorMessage.value"
                        autocomplete="off"
                        clearable
                      ></v-autocomplete>
                    </v-col>

                    <v-col lg="3" md="4" cols="12">
                      <v-autocomplete
                        label="Product"
                        variant="outlined"
                        density="compact"
                        bg-color="grey-lighten-4"
                        v-model="product_id.value.value"
                        :items="getProductMapList"
                        :item-title="(item) => item.product ? `${item.product.code ? item.product.code + ' - ' : ''} ${item.product.name || ''}`.trim() : ''"
                        item-value="product_id"
                        :error-messages="product_id.errorMessage.value"
                        autocomplete="off"
                        clearable
                      ></v-autocomplete>
                    </v-col>

                    <v-col lg="3" md="4" cols="12">
                      <v-select
                        label="Availability"
                        variant="outlined"
                        density="compact"
                        bg-color="grey-lighten-4"
                        v-model="availability.value.value"
                        :items="availabilityOptions"
                        item-title="text"
                        item-value="value"
                        :error-messages="availability.errorMessage.value"
                        clearable
                      ></v-select>
                    </v-col>

                    <v-col lg="3" md="12" cols="12">
                      <v-btn
                        class="bg-brand "
                        type="submit"
                        ref="searchButton"
                        >Search</v-btn
                      >
                    </v-col>
                  </v-row>
                </v-form>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>

    <v-container fluid style="margin-top: -20px;">
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title
                color="blue-grey-lighten-4"
                class="panel-header"
                >Inventory Records List</v-expansion-panel-title
              >
              <v-expansion-panel-text>
                <v-row class="py-2">
                  <v-col md="4" cols="12">
                    <v-text-field
                      v-model="search"
                      append-inner-icon="mdi-magnify"
                      label="Search"
                      single-line
                      hide-details
                      density="compact"
                      variant="outlined"
                      bg-color="grey-lighten-4"
                      class="pl-2"
                      clearable
                    ></v-text-field>
                  </v-col>

                  <v-col md="5" cols="12">
                    <div class="d-flex align-center flex-column">
                      <v-btn-group
                        variant="outlined"
                        density="compact"
                        size="x-small"
                        divided
                      >
                        <v-btn
                          prepend-icon="mdi mdi-download"
                          variant="flat"
                          color="success"
                          @click="exportToCSV"
                          >CSV</v-btn
                        >
                        <v-btn
                          prepend-icon="mdi mdi-download"
                          variant="flat"
                          color="primary"
                          @click="exportToExcel"
                          >EXCEL</v-btn
                        >
                        <v-btn
                          prepend-icon="mdi mdi-download"
                          variant="flat"
                          color="red"
                          @click="exportToPDF"
                          >PDF</v-btn
                        >
                      </v-btn-group>
                    </div>
                  </v-col>

                  <v-col md="3" cols="12">
                    <div class="text-right pr-2">
                      <v-chip color="primary" variant="tonal">
                        Total Records: {{ getSchema.length }}
                      </v-chip>
                    </div>
                  </v-col>
                </v-row>

                <v-data-table
                  :headers="headers"
                  :items="getSchema"
                  :loading="loading"
                  v-model:items-per-page="itemsPerPage"
                  :search="search"
                  :hover="true"
                  density="compact"
                >
                  <template v-slot:item.index="{ index }">
                    {{ ++index }}
                  </template>

                  <template v-slot:item.code="{ item }">
                    <span class="font-weight-medium">{{ item.code }}</span>
                  </template>

                  <template v-slot:item.batch_no="{ item }">
                    <v-chip size="small" color="info" variant="tonal">
                      {{ item.batch_no }}
                    </v-chip>
                  </template>

                  <template v-slot:item.warehouse="{ item }">
                    {{ item.warehouse?.name || "N/A" }}
                  </template>

                  <template v-slot:item.product_code="{ item }">
                    <span class="font-weight-medium">{{
                      item.product?.code || "N/A"
                    }}</span>
                  </template>

                  <template v-slot:item.product_name="{ item }">
                    {{ item.product?.name || "N/A" }}
                  </template>

                  <template v-slot:item.category="{ item }">
                    {{ item.product?.category?.name || "N/A" }}
                  </template>

                  <template v-slot:item.brand="{ item }">
                    {{ item.product?.brand?.name || "N/A" }}
                  </template>

                  <template v-slot:item.size="{ item }">
                    {{ item.product?.size?.name || "N/A" }}
                  </template>

                  <template v-slot:item.color="{ item }">
                    <v-chip
                      v-if="item.product?.color?.name"
                      size="small"
                      variant="tonal"
                    >
                      {{ item.product.color.name }}
                    </v-chip>
                    <span v-else>N/A</span>
                  </template>

                  <template v-slot:item.barcode="{ item }">
                    {{ item.product?.barcode || "N/A" }}
                  </template>

                  <template v-slot:item.quantity="{ item }">
                    <v-chip
                      :color="item.quantity > 0 ? 'success' : 'error'"
                      size="small"
                    >
                      {{ item.quantity }}
                    </v-chip>
                  </template>

                  <template v-slot:item.purchase_unit_price="{ item }">
                    {{ formatCurrency(item.purchase_unit_price) }}
                  </template>

                  <template v-slot:item.sales_unit_price="{ item }">
                    {{ formatCurrency(item.sales_unit_price) }}
                  </template>

                  <template v-slot:item.actions="{ item }">
                    <div class="d-flex justify-center align-center">
                      <v-tooltip text="Quick Summary" location="top">
                        <template v-slot:activator="{ props }">
                          <v-icon
                            v-bind="props"
                            class="mx-1"
                            color="primary"
                            @click="viewSummary(item)"
                          >
                            mdi-information-outline
                          </v-icon>
                        </template>
                      </v-tooltip>
                      <v-tooltip text="View Transaction Details" location="top">
                        <template v-slot:activator="{ props }">
                          <v-icon
                            v-bind="props"
                            class="mx-1"
                            color="info"
                            @click="viewDetails(item)"
                          >
                            mdi-eye-outline
                          </v-icon>
                        </template>
                      </v-tooltip>
                    </div>
                  </template>
                </v-data-table>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>

    <!-- Details Dialog -->
    <v-dialog v-model="detailsDialog" max-width="900px">
      <v-card>
        <v-card-title class="bg-blue-grey-lighten-4">
          <span class="text-h6">Inventory Record Details</span>
        </v-card-title>
        <v-card-text v-if="selectedItem" class="pt-4">
          <v-row>
            <v-col cols="12" md="6">
              <div class="mb-3">
                <strong>Inventory Code:</strong>
                <span class="ml-2">{{ selectedItem.code }}</span>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="mb-3">
                <strong>Batch No:</strong>
                <v-chip size="small" color="info" variant="tonal" class="ml-2">
                  {{ selectedItem.batch_no }}
                </v-chip>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="mb-3">
                <strong>Warehouse:</strong>
                <span class="ml-2">{{ selectedItem.warehouse?.name }}</span>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="mb-3">
                <strong>Product Code:</strong>
                <span class="ml-2">{{
                  selectedItem.product?.code
                }}</span>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="mb-3">
                <strong>Product Name:</strong>
                <span class="ml-2">{{ selectedItem.product?.name }}</span>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="mb-3">
                <strong>Category:</strong>
                <span class="ml-2">{{
                  selectedItem.product?.category?.name
                }}</span>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="mb-3">
                <strong>Brand:</strong>
                <span class="ml-2">{{ selectedItem.product?.brand?.name }}</span>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="mb-3">
                <strong>Size:</strong>
                <span class="ml-2">{{ selectedItem.product?.size?.name }}</span>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="mb-3">
                <strong>Color:</strong>
                <span class="ml-2">{{
                  selectedItem.product?.color?.name || "N/A"
                }}</span>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="mb-3">
                <strong>Product Barcode:</strong>
                <span class="ml-2">{{ selectedItem.product?.barcode }}</span>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="mb-3">
                <strong>Quantity:</strong>
                <v-chip
                  :color="selectedItem.quantity > 0 ? 'success' : 'error'"
                  size="small"
                  class="ml-2"
                >
                  {{ selectedItem.quantity }}
                </v-chip>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="mb-3">
                <strong>Purchase Rate:</strong>
                <span class="ml-2">{{
                  formatCurrency(selectedItem.purchase_unit_price)
                }}</span>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="mb-3">
                <strong>Sales Rate:</strong>
                <span class="ml-2">{{
                  formatCurrency(selectedItem.sales_unit_price)
                }}</span>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="mb-3">
                <strong>Created By:</strong>
                <span class="ml-2">{{
                  selectedItem.createdBy?.full_name || "N/A"
                }}</span>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="tonal" @click="detailsDialog = false">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script>
import { useStore } from "vuex";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { ref } from "vue";
import { toast } from "vue3-toastify";

export default {
  components: {},
  data: () => ({
    itemsPerPage: 10,
    panel: [0],
    loading: false,
    search: "",
    detailsDialog: false,
    selectedItem: null,
    availabilityOptions: [
      { text: "Available (In Stock)", value: "available" },
      { text: "Unavailable (Out of Stock)", value: "unavailable" },
    ],
    headers: [
      {
        title: "#SL",
        align: "center",
        key: "index",
        width: "80px",
        sortable: false,
      },
      {
        title: "Inventory Code",
        key: "code",
        align: "left",
        sortable: true,
        width: "150px",
      },
      {
        title: "Batch No",
        key: "batch_no",
        align: "left",
        sortable: true,
        width: "180px",
      },
      {
        title: "Warehouse",
        align: "left",
        key: "warehouse",
        sortable: true,
        width: "150px",
      },
      { title: "Project", key: "purpose.name", align: "left", sortable: false, width: "150px", },
      {
        title: "Product Code",
        align: "left",
        key: "product_code",
        sortable: true,
        width: "130px",
      },
      {
        title: "Product Name",
        align: "left",
        key: "product_name",
        sortable: true,
      },
      {
        title: "Category",
        align: "left",
        key: "category",
        sortable: true,
        width: "130px",
      },
      {
        title: "Brand",
        align: "left",
        key: "brand",
        sortable: true,
        width: "120px",
      },
      {
        title: "Size",
        align: "left",
        key: "size",
        sortable: true,
        width: "100px",
      },
      {
        title: "Color",
        align: "left",
        key: "color",
        sortable: true,
        width: "100px",
      },
      {
        title: "Product Barcode",
        align: "left",
        key: "barcode",
        sortable: true,
        width: "150px",
      },
      {
        title: "Quantity",
        align: "right",
        key: "quantity",
        sortable: true,
        width: "110px",
      },
      {
        title: "Purchase Rate",
        align: "right",
        key: "purchase_unit_price",
        sortable: true,
        width: "130px",
      },
      {
        title: "Sales Rate",
        align: "right",
        key: "sales_unit_price",
        sortable: true,
        width: "120px",
      },
      {
        title: "Action",
        align: "center",
        key: "actions",
        sortable: false,
        width: "120px",
      },
    ],
  }),
  watch: {},
  created() {
    if (this.$store.getters.getWarehouseList.length === 0) {
      this.$store.dispatch("getWarehouseList");
    }

    if (this.$store.getters.getProductMapList.length === 0) {
      this.$store.dispatch("getProductMapList");
    }
  },
  computed: {
    getSchema() {
      return this.$store.getters.getInventoryRecordsList || [];
    },
    getWarehouseList() {
      return this.$store.getters.getWarehouseList || [];
    },
    branchId() {
      return this.$store.getters.getAuthUser.branch_id || null;
    },
    getProductMapList() {
      let products = this.$store.getters.getProductMapList;
      return products.filter(
        (item) => item.branch_id == this.branchId && item.status == 1
      );
    },
  },
  setup() {
    const store = useStore();
    
    const { handleSubmit, resetForm, errorMessages } = useForm({
      validationSchema: yup.object({
        warehouse_id: yup
          .number()
          .positive()
          .integer()
          .nullable()
          .label("Warehouse"),
        product_id: yup
          .number()
          .positive()
          .integer()
          .nullable()
          .label("Product"),
        availability: yup
          .string()
          .nullable()
          .label("Availability"),
      }),
    });

    const warehouse_id = useField("warehouse_id");
    const product_id = useField("product_id");
    const availability = useField("availability");
    const loading = ref(false);

    const submit = handleSubmit((values) => {
      loading.value = true;
      const params = {};

      if (values.warehouse_id) {
        params.warehouse_id = values.warehouse_id;
      }

      if (values.product_id) {
        params.product_id = values.product_id;
      }

      if (values.availability) {
        params.availability = values.availability;
      }

      store
        .dispatch("getInventoryRecordsList", params)
        .then((response) => {
          loading.value = false;
          toast.success("Inventory records loaded successfully", {
            autoClose: 3000,
          });
        })
        .catch((error) => {
          loading.value = false;
          toast.error(
            "Failed to load inventory records: " +
              (error.response?.data?.message || error.message),
            {
              autoClose: 5000,
            }
          );
        });
    });

    return {
      warehouse_id,
      product_id,
      availability,
      submit,
      errorMessages,
      loading,
    };
  },
  methods: {
    viewSummary(item) {
      // Show quick summary dialog
      this.selectedItem = item;
      this.detailsDialog = true;
    },

    viewDetails(item) {
      // Navigate to inventory details page with query parameters
      const query = {
        product_id: item.product_id
      };
      
      // Add optional parameters if available
      if (item.warehouse_id) {
        query.warehouse_id = item.warehouse_id;
      }
      
      if (item.batch_no) {
        query.batch_no = item.batch_no;
      }
      
      this.$router.push({
        name: 'inventory-details',
        query: query
      });
    },

    formatCurrency(value) {
      if (!value) return "0.00";
      return parseFloat(value).toFixed(2);
    },

    exportToPDF() {
      let serialNumber = 1;
      const columns = [
        "#SL",
        "Inventory Code",
        "Batch No",
        "Warehouse",
        "Product Code",
        "Product Barcode",
        "Product Name",
        "Category",
        "Brand",
        "Size",
        "Color",
        "Quantity",
        "Purchase Rate",
        "Sales Rate",
      ];
      const rows = this.getSchema.map((item) => [
        serialNumber++,
        item.code,
        item.batch_no,
        item.warehouse?.name || "N/A",
        item.product?.product_code || "N/A",
        item.product?.barcode || "N/A",
        item.product?.name || "N/A",
        item.product?.category?.name || "N/A",
        item.product?.brand?.name || "N/A",
        item.product?.size?.name || "N/A",
        item.product?.color?.name || "N/A",
        item.quantity,
        this.formatCurrency(item.purchase_unit_price),
        this.formatCurrency(item.sales_unit_price),
      ]);
      this.$exportTableToPDF(
        columns,
        rows,
        "Inventory Records List",
        "inventory_records_" + this.$fileDate()
      );
    },

    exportToExcel() {
      var data = [
        [
          "#SL",
          "Inventory Code",
          "Batch No",
          "Warehouse",
          "Product Code",
          "Product Barcode",
          "Product Name",
          "Category",
          "Brand",
          "Size",
          "Color",
          "Quantity",
          "Purchase Rate",
          "Sales Rate",
        ],
      ];
      var serialNumber = 1;
      this.getSchema.forEach((item) => {
        data.push([
          serialNumber++,
          item.code,
          item.batch_no,
          item.warehouse?.name || "N/A",
          item.product?.product_code || "N/A",
          item.product?.barcode || "N/A",
          item.product?.name || "N/A",
          item.product?.category?.name || "N/A",
          item.product?.brand?.name || "N/A",
          item.product?.size?.name || "N/A",
          item.product?.color?.name || "N/A",
          item.quantity,
          this.formatCurrency(item.purchase_unit_price),
          this.formatCurrency(item.sales_unit_price),
        ]);
      });
      this.$exportTableToExcel(
        data,
        "inventory_records_" + this.$fileDate()
      );
    },

    exportToCSV() {
      var data = [
        [
          "#SL",
          "Inventory Code",
          "Batch No",
          "Warehouse",
          "Product Code",
          "Product Barcode",
          "Product Name",
          "Category",
          "Brand",
          "Size",
          "Color",
          "Quantity",
          "Purchase Rate",
          "Sales Rate",
        ],
      ];
      var serialNumber = 1;
      this.getSchema.forEach((item) => {
        data.push([
          serialNumber++,
          item.code,
          item.batch_no,
          item.warehouse?.name || "N/A",
          item.product?.product_code || "N/A",
          item.product?.barcode || "N/A",
          item.product?.name || "N/A",
          item.product?.category?.name || "N/A",
          item.product?.brand?.name || "N/A",
          item.product?.size?.name || "N/A",
          item.product?.color?.name || "N/A",
          item.quantity,
          this.formatCurrency(item.purchase_unit_price),
          this.formatCurrency(item.sales_unit_price),
        ]);
      });

      this.$exportTableToCSV(data, "inventory_records_" + this.$fileDate());
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.$refs.searchButton.$el.click();
    });
  },
};
</script>

<style scoped>
.panel-header {
  font-weight: 600;
}

.font-weight-medium {
  font-weight: 500;
}

.v-chip {
  font-weight: 500;
}
</style>
